import { useState, type FormEvent } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/da28uujk8r5te5mqsjrg';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('已收到，感谢你的关注！我们会尽快为你推送最新内容。');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim()) {
        payload.append(key, value.trim());
      }
    });

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      const failed = !res.ok || parsed?.code !== 'OK' || /spam/i.test(responseText) || /spam/i.test(serverMsg);
      if (failed) {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后再试。');
        return;
      }
      setStatus('success');
      setMessage('已收到，感谢你的关注！我们会尽快为你推送最新内容。');
      form.reset();
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 bg-background-900 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-4 py-1.5 text-sm font-semibold text-foreground-950 whitespace-nowrap">
              <i className="ri-mail-send-line" />
              追更通知
            </span>
            <h2 className="mt-5 font-heading text-3xl font-black text-background-50 md:text-4xl">
              订阅更新，第一时间追到新解析
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-background-100/90">
              留下你的邮箱，我们会在新文章上线、人气话题更新时第一时间通知你。也可以顺手写下你想看的专题，让社区往你想要的方向聊。
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              <a
                href="mailto:hello@heixiu-story.cn"
                className="inline-flex items-center gap-2 text-sm text-background-200 transition-colors hover:text-accent-300 whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                hello@heixiu-story.cn
              </a>
              <a
                href="#community"
                className="inline-flex items-center gap-2 text-sm text-background-200 transition-colors hover:text-accent-300 whitespace-nowrap"
              >
                <i className="ri-chat-smile-line" />
                直接去讨论区
              </a>
            </div>
          </div>

          <form
            id="subscribe-form"
            onSubmit={handleSubmit}
            data-readdy-form="da28uujk8r5te5mqsjrg"
            className="rounded-lg bg-background-50 p-6 ring-1 ring-background-200/70 md:p-8"
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="subscribe-name" className="text-sm font-medium text-foreground-800">
                  怎么称呼你
                </label>
                <input
                  id="subscribe-name"
                  name="name"
                  type="text"
                  placeholder="你的昵称"
                  className="rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="subscribe-email" className="text-sm font-medium text-foreground-800">
                  邮箱 *
                </label>
                <input
                  id="subscribe-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="subscribe-message" className="text-sm font-medium text-foreground-800">
                  想看的专题
                </label>
                <textarea
                  id="subscribe-message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="告诉我们你还想看哪方面的解析……"
                  className="resize-none rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <button
                type="submit"
                className="mt-1 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
              >
                订阅更新
              </button>

              {status === 'success' && (
                <p className="rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900" role="status">
                  {message}
                </p>
              )}
              {status === 'error' && (
                <p className="rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800" role="alert">
                  {message}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}